import { Router, ActivatedRoute } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { ServicesService } from './services.service';
import { Service } from '@nuso/portal/util-services';
import { map, tap } from 'rxjs/operators';
import { has } from 'lodash';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

export class ServiceStepper {
  isComplete: any = {};
  stepper: MatStepper;
  service: Service;
  steps: string[];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private servicesService: ServicesService,
  ) {
  }

  onInit(stepper: MatStepper, steps: string[]) {
    this.stepper = stepper;
    this.steps = steps;
  }

  onChanges(service: Service, provisionStep: string) {
    this.service = service;
    const wasAlreadyProvisioned = this.isComplete[provisionStep];
    this.isComplete = this.servicesService.allTasks(this.service).reduce((memo: Record<string, boolean>, t) => {
      memo[t.key] = t.state === 'COMPLETE';
      return memo;
    }, {} as Record<string, boolean>);

    return {
      wasAlreadyProvisioned,
      isProvisioned: this.isComplete[provisionStep]
    }
  }

  afterViewInit(internalStepMap: { [name: string]: string }) {
    this.route.queryParamMap
    .pipe(
      map(pMap => pMap.get('step'))
    ).subscribe(step => {
      if ( !this.stepper ) {
        return;
      }

      // if a known step was passed in, then set the selected index
      if (step && this.steps.indexOf(step) !== -1) {
        this.stepper.selectedIndex = this.steps.indexOf(step);
      } else {
        // first time in with no params (or url had the wrong step key)...
        // Look at the tasks and find the first that isn't complete
        // Based on this, set the current step
        const task = this.servicesService.findFirstIncompleteTask(this.service);
        if (task) {
          let nextStep = task.key;
          if (this.steps.indexOf(nextStep) === -1) {
            nextStep = internalStepMap[nextStep];
          }
          // The nextStep case really shouldn't happen, but in case we added some new steps
          // and did't update the code, at least default to the sip:settings step
          const nextStepIndex = nextStep ? this.steps.indexOf(nextStep) : 1;
          this.stepper.selectedIndex = nextStepIndex;
        }
      }
    })
  }

  // Not really skipping, just marking complete in the GUI
  skipStep(stepName: string) {
    const currentStep = this.stepper.selected;
    if (!currentStep) {
      throw new Error('skipStep called with no selected step');
    }
    currentStep.completed = this.isComplete[stepName] = true;
    this.stepper.next();
  }

  findTask(taskKey: string) {
    return this.service && this.servicesService.findTask(this.service, taskKey);
  }

  completeTask(taskKey: string, status = 'complete', statusDescription = 'task is complete', redirectTo: string | null = null) {
    const task = this.servicesService.findTask(this.service, taskKey);
    if (task) {
      this.servicesService.updateServiceTaskByKey({
        serviceId: this.service.id,
        taskKey,
        state: 'COMPLETE',
        status,
        statusDescription
      })
      .pipe(
        tap(() => {
          if (redirectTo) {
            this.router.navigateByUrl(redirectTo)
          }
        })
      )
      .subscribe();
    } else {
      console.log('no task found for', taskKey);
    }
  }

  onStepChange(event: StepperSelectionEvent) {
    const step = this.steps[event.selectedIndex];
    if (step) {
      this.router.navigate([], { queryParams: { step }})
    }
  }

  hasTask(task: string) {
    return this.isComplete && has(this.isComplete, task);
  }

}